import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { BankingService } from 'services';
import DateTime from 'utils/date';
import { ITransaction } from 'common/transactions.interface';
import { EAccountType, IAccountDashboardBalance } from 'common/accounts.interface';
import { formatNumberToBRL } from 'utils/formatters';
import { useSelector } from 'react-redux';
import { IRootState } from 'stores';
import { BankingState } from 'stores/banking';
import { useBankingVisibility } from '../../contexts/BankingVisibilityContext';
import { SummaryTable } from './components/SummaryTable';
import { SummaryBillets } from './components/SummaryBillets';
import { SummaryTariff } from './components/SummaryTariff';

const SummaryPage: React.FC = () => {
  const [showTariff] = React.useState(false);
  const { showValues, toggleVisibility } = useBankingVisibility();
  const navigate = useNavigate();
  const [todayTransactions, setTodayTransactions] = React.useState<ITransaction[]>([]);
  const [loadingToday, setLoadingToday] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [hasMore, setHasMore] = React.useState(true);
  const [dashboardBalances, setDashboardBalances] = React.useState<IAccountDashboardBalance[] | null>(null);

  const PAGE_SIZE = 20;

  const { accounts } = useSelector<IRootState, BankingState>(({ banking }) => banking);
  const paymentAccount = React.useMemo(() => accounts?.find((a: any) => a.accountType === EAccountType.PAYMENT), [accounts]);

  const loadPage = React.useCallback(
    async (targetPage: number) => {
      if (!paymentAccount?.id) return;

      try {
        setLoadingToday(true);

        const bankingService = new BankingService();

        const today = DateTime.now().toISODate();

        const response = await bankingService.getTransactions({
          accountType: EAccountType.PAYMENT,
          page: targetPage,
          limit: PAGE_SIZE,
          startDate: `${today}T00:00:00`,
          endDate: `${today}T23:59:59`,
        });

        const items = Array.isArray(response.items) ? response.items : [];
        setTodayTransactions((prev) => (targetPage === 1 ? items : [...prev, ...items]));
        setHasMore(response.page * response.limit < response.total);
      } catch (e) {
        if (targetPage === 1) setTodayTransactions([]);
        setHasMore(false);
      } finally {
        setLoadingToday(false);
      }
    },
    [paymentAccount?.id]
  );

  React.useEffect(() => {
    loadPage(page);
  }, [page, loadPage]);

  React.useEffect(() => {
    const loadBalances = async () => {
      try {
        const service = new BankingService();
        const data = await service.getDashboardBalance();
        setDashboardBalances(data || []);
      } catch (err) {
        setDashboardBalances([]);
      }
    };
    loadBalances();
  }, []);

  const paymentBalance = React.useMemo(() => dashboardBalances?.find((b) => b.accountType === EAccountType.PAYMENT), [dashboardBalances]);
  const controlBalance = React.useMemo(() => dashboardBalances?.find((b) => b.accountType === EAccountType.CONTROL), [dashboardBalances]);

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', width: '100%', gap: '1vw' }}>
        <Box sx={{ flex: 1, border: '1px solid #E9E8E7', borderRadius: '8px', p: '12px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box
              sx={{
                backgroundColor: '#EBEBFF',
                border: '1px solid #D7D8FA',
                borderRadius: '12px',
                px: '8px',
                py: '4px',
                height: '25px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography component="span" sx={{ color: '#315ED8', fontSize: '14px', fontWeight: 400 }}>
                Conta movimento
              </Typography>
            </Box>

            <Typography
              component="span"
              sx={{
                color: '#115398',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
              onClick={() => navigate('/banking/dashboard/balance', { state: { accountType: 'PAYMENT' } })}
            >
              Ir para o extrato
              <ArrowForwardIcon sx={{ color: '#115398', fontSize: 16, ml: '5px' }} />
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
            <Typography component="span" sx={{ fontSize: '14px', fontWeight: 400, color: '#434241' }}>
              Saldo em conta
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography component="span" sx={{ fontSize: '24px', fontWeight: 600, color: '#434241' }}>
                {showValues ? formatNumberToBRL(paymentBalance?.balance || 0) : '••••••'}
              </Typography>
              <IconButton size="small" onClick={toggleVisibility}>
                {showValues ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, mt: 1, gap: '1vw' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '4px',
                border: '0.4px solid #F7F6F6',
                p: '10px',
                flex: 1,
              }}
            >
              <Box
                sx={{
                  backgroundColor: '#F7F6F6',
                  border: '1px solid #E9E8E7',
                  borderRadius: '12px',
                  px: '8px',
                  py: '4px',
                  height: '25px',
                  width: 'fit-content',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography component="span" sx={{ color: '#656464', fontSize: '14px', fontWeight: 400, lineHeight: 1 }}>
                  Entradas do dia
                </Typography>
              </Box>
              <Typography component="span" sx={{ ml: '8px', fontSize: '14px', mt: '5px' }}>
                {showValues ? formatNumberToBRL(paymentBalance?.entriesTotal || 0) : '••••••'}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '4px',
                border: '0.4px solid #F7F6F6',
                p: '10px',
                flex: 1,
              }}
            >
              <Box
                sx={{
                  backgroundColor: '#F7F6F6',
                  border: '1px solid #E9E8E7',
                  borderRadius: '12px',
                  px: '8px',
                  py: '4px',
                  width: 'fit-content',
                  height: '25px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography component="span" sx={{ color: '#656464', fontSize: '14px', fontWeight: 400, lineHeight: 1 }}>
                  Saídas do dia
                </Typography>
              </Box>
              <Typography component="span" sx={{ ml: '8px', fontSize: '14px', mt: '5px' }}>
                {showValues ? formatNumberToBRL(controlBalance?.exitsTotal || 0) : '••••••'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ flex: 1, border: '1px solid #E9E8E7', borderRadius: '8px', p: '12px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box
              sx={{
                backgroundColor: '#EBEBFF',
                border: '1px solid #D7D8FA',
                borderRadius: '12px',
                px: '8px',
                py: '4px',
                height: '25px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography component="span" sx={{ color: '#315ED8', fontSize: '14px', fontWeight: 400 }}>
                Conta recebimento
              </Typography>
            </Box>

            <Typography
              component="span"
              sx={{
                color: '#115398',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
              onClick={() => navigate('/banking/dashboard/balance', { state: { accountType: 'CONTROL' } })}
            >
              Ir para o extrato
              <ArrowForwardIcon sx={{ color: '#115398', fontSize: 16, ml: '5px' }} />
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
            <Typography component="span" sx={{ fontSize: '14px', fontWeight: 400, color: '#434241' }}>
              Saldo em conta
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography component="span" sx={{ fontSize: '24px', fontWeight: 600, color: '#434241' }}>
                {showValues ? formatNumberToBRL(controlBalance?.balance || 0) : '••••••'}
              </Typography>
              <IconButton size="small" onClick={toggleVisibility}>
                {showValues ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, mt: 1, gap: '1vw' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '4px',
                border: '0.4px solid #F7F6F6',
                p: '10px',
                flex: 1,
              }}
            >
              <Box
                sx={{
                  backgroundColor: '#F7F6F6',
                  border: '1px solid #E9E8E7',
                  borderRadius: '12px',
                  px: '8px',
                  py: '4px',
                  width: 'fit-content',
                  height: '25px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography component="span" sx={{ color: '#656464', fontSize: '14px', fontWeight: 400, lineHeight: 1 }}>
                  Entradas do dia
                </Typography>
              </Box>
              <Typography component="span" sx={{ ml: '8px', fontSize: '14px', mt: '5px' }}>
                {showValues ? formatNumberToBRL(controlBalance?.entriesTotal || 0) : '••••••'}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '4px',
                border: '0.4px solid #F7F6F6',
                p: '10px',
                flex: 1,
              }}
            >
              <Box
                sx={{
                  backgroundColor: '#F7F6F6',
                  border: '1px solid #E9E8E7',
                  borderRadius: '12px',
                  px: '8px',
                  py: '4px',
                  width: 'fit-content',
                  height: '25px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography component="span" sx={{ color: '#656464', fontSize: '14px', fontWeight: 400, lineHeight: 1 }}>
                  Saídas do dia
                </Typography>
              </Box>
              <Typography component="span" sx={{ ml: '8px', fontSize: '14px', mt: '5px' }}>
                {showValues ? formatNumberToBRL(controlBalance?.exitsTotal || 0) : '••••••'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <SummaryBillets />

      <SummaryTable
        transactions={todayTransactions}
        loading={loadingToday}
        hasMore={hasMore}
        onLoadMore={() => {
          if (!loadingToday && hasMore) setPage((p) => p + 1);
        }}
      />

      {showTariff && <SummaryTariff />}
    </>
  );
};

export default SummaryPage;
